/*
  Invoice drawer (UI overhaul wave 2, screen 07 idea 6) — Add invoice, Edit invoice and "Raise invoice"
  (turn a scheduled payment into an invoice) in one right-side panel; full screen on phones, so "Add
  Invoice" is never cut off there.

  - Types as toggle chips (one invoice can cover several terms, then a % per type that must make 100%).
  - The amount is checked as typed ('22,209.00' ok, '22,209,0' not) and the drawer warns when the invoices of
    the scope would add up to more than the scope value — before saving.
  - Photos show as previews (not an "Open" link); a new photo can be picked for either slot.
  - "Submitted on" (wave 2 integration): a submitted / paid invoice shows the day it was sent; invoices raised
    before that day was recorded can get their real date, so the Invoicing register's age is exact, not "≈".
  - Wave 3 (07 #8): "Due date" on every invoice (for a scheduled row: when it should be invoiced; retention
    defaults to the handover date + the 12-month defects period) and "Paid on" for a paid invoice.
    Raising a scheduled row (or switching it to Submitted / Paid) starts with an EMPTY due date: the row's
    "invoice by" day is not the day the client must pay — keeping it would show the new invoice as overdue at once.
  Nothing is saved until "Save"; closing with changes asks first (SidePanel).
  - Read only (28 #8): without `onSave` (a role whose Invoicing is View only opens "Open invoice") every field is
    read-only and the photos only open: no Choose… / Replace… / Remove, no type chips or status to change.
  - BUG-006: an invoice someone asked to delete says so at the top (who, when, why); an Admin approves or rejects it
    from the row's ⋯ menu.
*/
import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ImagePlus, Trash2, TriangleAlert } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SidePanel, SidePanelSection } from '@/components/ui/side-panel';
import { StatusPill } from '@/components/ui/status-pill';
import { cn } from '@/lib/utils';
import { formatDate, formatNumber, toDateInputValue } from '@/lib/format';
import { MONO } from './ProjectUi';
import { splitTypes, validateInvoiceDraft } from './invoicingModel';
import { deletionRequestOf } from '@/components/supply-finance/invoiceDeletionModel';

const LABEL = 'text-[12px] font-semibold text-ink-2';
const RAISED = new Set(['Submitted', 'Payment Received']);
const localToday = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const FieldError = ({ children, id }) => (children ? <p id={id} className="text-[12px] font-semibold text-status-red-ink" role="alert">{children}</p> : null);

export function formFromInvoice(invoice, mode) {
  const types = splitTypes(invoice?.invoice_type);
  const percentages = {};
  Object.entries(invoice?.invoice_type_percentages || {}).forEach(([type, value]) => { percentages[type] = String(value); });
  const status = invoice?.status || 'Not Started';
  const raising = mode === 'raise' && status === 'Not Started';
  return {
    types,
    percentages,
    status: raising ? 'Submitted' : status,
    amount: invoice ? formatNumber(invoice.amount ?? 0, { decimals: 2 }) : '',
    reference: invoice?.reference || '',
    note: invoice?.note || '',
    submittedAt: toDateInputValue(invoice?.submitted_at) || '',
    // a scheduled row's due date is when to INVOICE it; once raised the field means when the client must pay
    dueDate: raising ? '' : (toDateInputValue(invoice?.due_date) || ''),
    paidAt: toDateInputValue(invoice?.paid_at) || '',
  };
}

/** A stored invoice photo (authenticated) as an <img>. */
function StoredPhoto({ url, alt }) {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!url) return undefined;
    let objectUrl = '';
    let cancelled = false;
    const apiPath = String(url).startsWith('/api/') ? String(url).slice(4) : String(url);
    api.get(apiPath, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);
  if (failed) return <span className="text-[12px] text-ink-3">Could not load the photo</span>;
  if (!src) return <span className="ui-skeleton-shimmer block h-full w-full rounded-[10px]" role="status"><span className="sr-only">Loading photo</span></span>;
  return <img src={src} alt={alt} className="h-full w-full rounded-[10px] object-cover" />;
}

function PhotoSlot({ label, storedUrl, file, onPick, onOpenStored, testId, readOnly = false }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState('');
  useEffect(() => {
    if (!file) { setPreview(''); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const hasPhoto = Boolean(preview || storedUrl);
  if (readOnly) {
    // 28 #8: a stored photo opens; there is nothing to choose, replace or remove
    return (
      <div className="flex min-w-0 flex-col gap-1.5" data-testid={testId}>
        <span className={LABEL}>{label}</span>
        {storedUrl ? (
          <button
            type="button"
            onClick={() => onOpenStored?.(storedUrl)}
            className="grid h-[124px] place-items-center overflow-hidden rounded-[12px] border border-line bg-surface-2 text-[12.5px] text-ink-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Open ${label}`}
          >
            <StoredPhoto url={storedUrl} alt={label} />
          </button>
        ) : (
          <span className="grid h-[124px] place-items-center rounded-[12px] border border-dashed border-[color:var(--line-dashed)] text-[12.5px] text-ink-3">
            No photo
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="flex min-w-0 flex-col gap-1.5" data-testid={testId}>
      <span className={LABEL}>{label}</span>
      <button
        type="button"
        onClick={() => (hasPhoto && !preview && onOpenStored ? onOpenStored(storedUrl) : inputRef.current?.click())}
        className={cn(
          'grid h-[124px] place-items-center overflow-hidden rounded-[12px] border text-[12.5px] text-ink-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          hasPhoto ? 'border-line bg-surface-2' : 'border-dashed border-[color:var(--line-dashed)] hover:bg-surface-hover',
        )}
        aria-label={hasPhoto ? `Open ${label}` : `Choose ${label}`}
      >
        {preview ? <img src={preview} alt={`${label} (new)`} className="h-full w-full object-cover" />
          : storedUrl ? <StoredPhoto url={storedUrl} alt={label} />
            : <span className="flex flex-col items-center gap-1"><ImagePlus className="h-5 w-5" aria-hidden="true" />Choose a photo</span>}
      </button>
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="xs" onClick={() => inputRef.current?.click()}>
          <ImagePlus aria-hidden="true" />
          {hasPhoto ? 'Replace…' : 'Choose…'}
        </Button>
        {file ? <span className="min-w-0 truncate text-[11.5px] text-status-amber-ink">New: {file.name}</span> : null}
        {file ? <Button type="button" variant="ghost" size="xs" onClick={() => onPick(null)}>Remove</Button> : null}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(event) => onPick(event.target.files?.[0] || null)} aria-label={`${label} file`} />
    </div>
  );
}

/** BUG-006: "Deletion requested by Mona Presales · 4 Oct 2026 — “duplicate”" at the top of the drawer. */
function DeletionRequestNote({ request }) {
  const reason = String(request.reason || '').trim();
  return (
    <div className="flex items-start gap-2 rounded-xl border border-[color:var(--danger-border)] bg-status-red-bg px-3 py-2.5 text-[12.5px] leading-snug text-status-red-ink" role="note" data-testid="invoice-panel-deletion-request">
      <Trash2 className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="min-w-0">
        <b className="font-semibold">Deletion requested by {request.requested_by_name || 'someone'}</b>
        {request.requested_at ? ` · ${formatDate(request.requested_at)}` : ''}
        {reason ? <> — “{reason}”</> : null}
        <span className="block text-ink-2">It stays in the totals until an Admin approves it from the row’s ⋯ menu.</span>
      </span>
    </div>
  );
}

export function InvoicePanel({
  open,
  onOpenChange,
  mode = 'edit',
  invoice,
  scopeLabel,
  scopeValue = 0,
  otherTotal = 0,
  typeOptions = [],
  statusOptions = [],
  onSave,
  onOpenPhoto,
  focus,
  scheduledCount = 0,
  retentionDueHint = '',
  readOnly: readOnlyProp,
}) {
  // 28 #8: no Save (a View-only Invoicing role) = a read-only drawer, unless the caller says otherwise
  const readOnly = readOnlyProp ?? !onSave;
  const [form, setForm] = useState(() => formFromInvoice(invoice, mode));
  const [baseline, setBaseline] = useState(() => JSON.stringify(formFromInvoice(invoice, mode))); // raise: the preset
  const [files, setFiles] = useState({ reference: null, invoice: null });
  const [showErrors, setShowErrors] = useState(false);
  const photosRef = useRef(null);
  const numberRef = useRef(null);
  const invoiceId = invoice?.id || '';

  useEffect(() => {
    if (!open) return undefined;
    const initial = formFromInvoice(invoice, mode);
    // Raising a scheduled payment starts from its row preset as a submitted invoice. That preset is the drawer's
    // starting point, not an edit: "unsaved changes" (and the leave guard) count only what the person changes.
    // Saving still sends status Submitted (save() always sends the form's status).
    setBaseline(JSON.stringify(initial));
    setForm(initial);
    setFiles({ reference: null, invoice: null });
    setShowErrors(false);
    const timer = window.setTimeout(() => {
      if (focus === 'photos') photosRef.current?.scrollIntoView({ block: 'center' });
      else if (mode === 'raise') numberRef.current?.focus();
    }, 150);
    return () => window.clearTimeout(timer);
    // A fresh form each time the drawer opens (or opens on another invoice).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoiceId, mode]);

  const check = useMemo(() => validateInvoiceDraft(form, {
    scopeLabel,
    scopeValue,
    otherTotal,
    requireNumber: mode === 'raise',
  }), [form, scopeLabel, scopeValue, otherTotal, mode]);

  const changedFields = (() => {
    let count = 0;
    try {
      const before = JSON.parse(baseline);
      Object.keys(form).forEach((key) => { if (JSON.stringify(form[key]) !== JSON.stringify(before[key])) count += 1; });
    } catch {
      count = 0;
    }
    return count + (files.reference ? 1 : 0) + (files.invoice ? 1 : 0);
  })();

  const toggleType = (type) => setForm((prev) => {
    const selected = prev.types.includes(type) ? prev.types.filter((entry) => entry !== type) : [...prev.types, type];
    const percentages = {};
    selected.forEach((entry) => { if (prev.percentages[entry] !== undefined) percentages[entry] = prev.percentages[entry]; });
    return { ...prev, types: selected, percentages };
  });

  // "Submitted on" — only when editing a raised invoice (a new or raised invoice gets today from the server).
  const showSubmittedOn = mode === 'edit' && RAISED.has(form.status);
  const storedSubmittedOn = toDateInputValue(invoice?.submitted_at) || '';
  const submittedOnError = showSubmittedOn && form.submittedAt && form.submittedAt > localToday()
    ? 'Submitted on cannot be in the future'
    : '';
  // 07 #8: "Paid on" for a paid invoice (the server stamps today when it becomes Payment Received).
  const showPaidOn = mode === 'edit' && form.status === 'Payment Received';
  const storedPaidOn = toDateInputValue(invoice?.paid_at) || '';
  const storedDueDate = toDateInputValue(invoice?.due_date) || '';
  // The scheduled row's "invoice by" day, while it is being raised (shown as a hint, never kept as the due date)
  const plannedInvoiceBy = (invoice?.status || 'Not Started') === 'Not Started' && RAISED.has(form.status) ? storedDueDate : '';
  const paidOnError = showPaidOn && form.paidAt
    ? (form.paidAt > localToday() ? 'Paid on cannot be in the future'
      : (form.submittedAt && form.paidAt < form.submittedAt ? 'Paid on cannot be before the invoice was submitted' : ''))
    : '';

  const save = async () => {
    setShowErrors(true);
    if (!check.ok) {
      const first = Object.values(check.errors)[0];
      throw new Error(first || 'Check the highlighted fields');
    }
    if (submittedOnError) throw new Error(submittedOnError);
    if (paidOnError) throw new Error(paidOnError);
    const submittedOnChanged = showSubmittedOn && form.submittedAt && form.submittedAt !== storedSubmittedOn;
    const dueChanged = mode === 'add' ? Boolean(form.dueDate) : form.dueDate !== storedDueDate;
    const paidOnChanged = showPaidOn && form.paidAt && form.paidAt !== storedPaidOn;
    await onSave?.({
      invoice_type: form.types.join(', '),
      invoice_type_percentages: check.percentages,
      status: form.status,
      amount: check.amount,
      reference: form.reference.trim(),
      note: form.note.trim(),
      ...(submittedOnChanged ? { submitted_at: form.submittedAt } : {}),
      ...(dueChanged ? { due_date: form.dueDate } : {}),
      ...(paidOnChanged ? { paid_at: form.paidAt } : {}),
      files,
    });
  };

  const title = mode === 'add' ? 'Add invoice' : mode === 'raise' ? 'Raise invoice' : `Invoice ${invoice?.reference || ''}`.trim();
  const errors = showErrors ? check.errors : {};
  // The amount is checked while typing (as soon as there is something in the field), the rest on Save.
  const amountError = check.errors.amount && (showErrors || String(form.amount).trim()) ? check.errors.amount : '';
  const sharePreview = check.amount !== null && check.amount !== undefined && scopeValue > 0
    ? `${formatNumber((check.amount / scopeValue) * 100, { decimals: 2 })}% of ${scopeLabel}`
    : '';

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="md"
      title={title}
      subtitle={mode === 'raise'
        ? `Turns the scheduled ${form.types.join(', ') || 'payment'} of ${scopeLabel} into an invoice`
        : `${scopeLabel}${mode === 'edit' && invoice?.invoice_type ? ` · ${invoice.invoice_type}` : ''}`}
      status={mode === 'add' ? null : <StatusPill status={form.status} domain="invoice" />}
      dirty={changedFields}
      onSave={onSave && !readOnly ? save : undefined}
      saveLabel={mode === 'add' ? 'Add invoice' : mode === 'raise' ? 'Raise invoice' : 'Save invoice'}
      cancelLabel={readOnly ? 'Close' : 'Cancel'}
      saveDisabled={mode !== 'add' && mode !== 'raise' && changedFields === 0}
      confirmOptions={{
        title: 'Discard this invoice’s changes?',
        description: 'What you typed in the drawer will be lost.',
        confirmLabel: 'Discard changes',
        cancelLabel: 'Keep editing',
      }}
      testId="invoice-panel"
    >
      {mode !== 'add' && deletionRequestOf(invoice) ? <DeletionRequestNote request={deletionRequestOf(invoice)} /> : null}
      <SidePanelSection title="Invoice">
        <div className="flex flex-col gap-1.5">
          <span className={LABEL} id="invoice-panel-types">Type</span>
          <div className="flex flex-wrap gap-1.5" role="group" aria-labelledby="invoice-panel-types">
            {typeOptions.map((type) => {
              const selected = form.types.includes(type);
              return (
                <button
                  key={type}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleType(type)}
                  disabled={readOnly}
                  className={cn(
                    'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default',
                    selected ? 'border-[color:var(--brand)] bg-brand-soft text-[color:var(--brand-ink)]' : 'border-line bg-surface text-ink-2 enabled:hover:bg-surface-hover disabled:text-ink-3',
                  )}
                >
                  {selected ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : null}
                  {type}
                </button>
              );
            })}
          </div>
          <FieldError>{errors.types}</FieldError>
        </div>
        {form.types.length > 1 ? (
          <div className="flex flex-col gap-1.5">
            <span className={LABEL}>Split between the types (must make 100%)</span>
            <div className="grid gap-2 sm:grid-cols-2">
              {form.types.map((type) => (
                <label key={type} className="flex items-center gap-2 text-[12.5px] text-ink-2">
                  <span className="min-w-0 flex-1 truncate">{type}</span>
                  <Input
                    inputMode="decimal"
                    value={form.percentages[type] ?? ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, percentages: { ...prev.percentages, [type]: event.target.value } }))}
                    className="w-20 text-right"
                    aria-label={`${type} percentage`}
                    readOnly={readOnly}
                  />
                  <span>%</span>
                </label>
              ))}
            </div>
            <FieldError>{errors.percentages}</FieldError>
          </div>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="invoice-panel-status" className={LABEL}>Status</label>
            <Select
              value={form.status}
              onValueChange={(value) => setForm((prev) => {
                const next = { ...prev, status: value };
                // Not Started → Submitted / Paid: the planned "invoice by" day is not a payment due date
                if (prev.status === 'Not Started' && RAISED.has(value) && prev.dueDate && prev.dueDate === storedDueDate) next.dueDate = '';
                // back to Not Started before saving: the row keeps its planned day
                if (value === 'Not Started' && (invoice?.status || 'Not Started') === 'Not Started' && !prev.dueDate) next.dueDate = storedDueDate;
                return next;
              })}
              disabled={readOnly}
            >
              <SelectTrigger id="invoice-panel-status" aria-label="Invoice status" className={readOnly ? 'disabled:cursor-default disabled:opacity-100' : undefined}><SelectValue /></SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="invoice-panel-number" className={LABEL}>Invoice number</label>
            <Input
              id="invoice-panel-number"
              ref={numberRef}
              value={form.reference}
              onChange={(event) => setForm((prev) => ({ ...prev, reference: event.target.value }))}
              className={MONO}
              placeholder={readOnly ? '' : 'e.g. 690'}
              aria-invalid={Boolean(errors.reference) || undefined}
              readOnly={readOnly}
            />
            <FieldError>{errors.reference}</FieldError>
          </div>
        </div>
        {showSubmittedOn ? (
          <div className="flex flex-col gap-1.5" data-testid="invoice-panel-submitted-on">
            <label htmlFor="invoice-panel-submitted-at" className={LABEL}>Submitted on</label>
            <Input
              id="invoice-panel-submitted-at"
              type="date"
              max={localToday()}
              value={form.submittedAt}
              onChange={(event) => setForm((prev) => ({ ...prev, submittedAt: event.target.value }))}
              readOnly={readOnly}
              className="sm:w-[200px]"
              aria-invalid={Boolean(submittedOnError) || undefined}
              aria-describedby="invoice-panel-submitted-note"
            />
            {submittedOnError ? (
              <FieldError id="invoice-panel-submitted-note">{submittedOnError}</FieldError>
            ) : !storedSubmittedOn ? (
              <p id="invoice-panel-submitted-note" className="text-[12px] text-ink-3">
                Not recorded for this invoice — the Invoicing register estimates its age (≈).{readOnly ? '' : ' Enter the day it was sent.'}
              </p>
            ) : (
              <p id="invoice-panel-submitted-note" className="sr-only">The Invoicing register counts the invoice’s age from this day.</p>
            )}
          </div>
        ) : null}
        <div className={cn('grid gap-3', showPaidOn && 'sm:grid-cols-2')}>
          <div className="flex flex-col gap-1.5" data-testid="invoice-panel-due-date">
            <label htmlFor="invoice-panel-due" className={LABEL}>{form.status === 'Not Started' ? 'Invoice by (due)' : 'Due date'}</label>
            <div className="flex items-center gap-2">
              <Input
                id="invoice-panel-due"
                type="date"
                value={form.dueDate}
                onChange={(event) => setForm((prev) => ({ ...prev, dueDate: event.target.value }))}
                readOnly={readOnly}
                className="sm:w-[200px]"
                aria-describedby="invoice-panel-due-note"
              />
              {form.dueDate && !readOnly ? (
                <Button type="button" variant="ghost" size="xs" onClick={() => setForm((prev) => ({ ...prev, dueDate: '' }))}>Clear</Button>
              ) : null}
            </div>
            <p id="invoice-panel-due-note" className="text-[12px] text-ink-3">
              {readOnly && !form.dueDate
                ? 'No due date recorded.'
                : form.dueDate
                ? 'Shown as “overdue” on the row after this day; Invoicing gets a reminder.'
                : plannedInvoiceBy
                  ? `Planned to be invoiced by ${formatDate(plannedInvoiceBy)} — enter when the client must pay (optional).`
                  : (retentionDueHint || (form.status === 'Not Started' ? 'When this payment should be invoiced.' : 'When the client should pay.'))}
            </p>
          </div>
          {showPaidOn ? (
            <div className="flex flex-col gap-1.5" data-testid="invoice-panel-paid-on">
              <label htmlFor="invoice-panel-paid" className={LABEL}>Paid on</label>
              <Input
                id="invoice-panel-paid"
                type="date"
                max={localToday()}
                value={form.paidAt}
                onChange={(event) => setForm((prev) => ({ ...prev, paidAt: event.target.value }))}
                readOnly={readOnly}
                className="sm:w-[200px]"
                aria-invalid={Boolean(paidOnError) || undefined}
                aria-describedby="invoice-panel-paid-note"
              />
              {paidOnError
                ? <FieldError id="invoice-panel-paid-note">{paidOnError}</FieldError>
                : <p id="invoice-panel-paid-note" className="text-[12px] text-ink-3">{storedPaidOn ? 'Used for DSO (days to get paid).' : `Not recorded yet${readOnly ? '.' : ' — enter the day the money arrived.'}`}</p>}
            </div>
          ) : null}
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="invoice-panel-amount" className={cn(LABEL, 'flex items-center gap-2')}>
            Amount (AED)
            {sharePreview ? <span className="ml-auto text-[11.5px] font-medium text-ink-3">{sharePreview}</span> : null}
          </label>
          <Input
            id="invoice-panel-amount"
            inputMode="decimal"
            value={form.amount}
            onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value }))}
            onBlur={() => {
              if (check.amount !== null && check.amount !== undefined) setForm((prev) => ({ ...prev, amount: formatNumber(check.amount, { decimals: 2 }) }));
            }}
            readOnly={readOnly}
            className="text-right tabular-nums"
            placeholder="0.00"
            aria-invalid={Boolean(amountError) || undefined}
            aria-describedby="invoice-panel-amount-note"
          />
          <FieldError id="invoice-panel-amount-note">{amountError}</FieldError>
        </div>
        {check.warnings.length ? (
          <div className="flex flex-col gap-1.5 rounded-[12px] border border-[color:color-mix(in_srgb,var(--status-amber)_40%,transparent)] bg-status-amber-bg px-3 py-2.5" role="status" data-testid="invoice-panel-warnings">
            {check.warnings.map((warning) => (
              <p key={warning} className="flex items-start gap-2 text-[12.5px] leading-snug text-ink">
                <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-amber" aria-hidden="true" />
                {warning}
              </p>
            ))}
            {mode === 'add' && scheduledCount > 0 && check.warnings.some((warning) => warning.startsWith('Invoices of')) ? (
              <p className="pl-[22px] text-[12px] text-ink-2">
                This scope still has {scheduledCount} scheduled payment{scheduledCount === 1 ? '' : 's'}: use “Raise invoice” on that row instead of adding a new one.
              </p>
            ) : null}
          </div>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="invoice-panel-note" className={LABEL}>Note</label>
          <Textarea id="invoice-panel-note" rows={2} value={form.note} onChange={(event) => setForm((prev) => ({ ...prev, note: event.target.value }))} readOnly={readOnly} />
        </div>
      </SidePanelSection>

      <div ref={photosRef}>
        <SidePanelSection title="Photos">
          <div className="grid gap-3 sm:grid-cols-2">
            <PhotoSlot
              label="Prerequisites photo"
              storedUrl={invoice?.invoice_reference_photo_url}
              file={files.reference}
              onPick={(file) => setFiles((prev) => ({ ...prev, reference: file }))}
              onOpenStored={onOpenPhoto}
              readOnly={readOnly}
              testId="invoice-photo-reference"
            />
            <PhotoSlot
              label="Invoice photo"
              storedUrl={invoice?.invoice_photo_url}
              file={files.invoice}
              onPick={(file) => setFiles((prev) => ({ ...prev, invoice: file }))}
              onOpenStored={onOpenPhoto}
              readOnly={readOnly}
              testId="invoice-photo-invoice"
            />
          </div>
        </SidePanelSection>
      </div>
    </SidePanel>
  );
}

